import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  colors,
  css,
  fonts,
  getGraphColor,
  icons,
  Units,
  useUnitsComponent,
} from "../../../Styles";
import {
  // Activations,
  NewModal,
  FeedCurveViewer,
  NewSelect,
  ProgressBar,
} from "../..";
import { NewFieldStyles } from "../../../Definitions";
import { useAppSelector } from "../../../Store";
import { Graph_QL } from "../../../Services";
import { useAnimal } from "../../../Services/Agrisys_GqlDataCaller/AnimalDataCalls/useAnimal";
import { useSection } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/useSections";
import { useSystem } from "../../../Services/Agrisys_GqlDataCaller/SystemCalls/useSystem";
import { useFeedCurves } from "../../../Services/Agrisys_GqlDataCaller/FeedDataCalls/useFeedCurves";

// TODO
import FeedCorrectionPopover from "../../LocationFeeder/feedCorrectionPopover";
import FeedSkipPopover from "../../LocationFeeder/feedSkipPopover";
import { LfLocation } from "../../../Services/Agrisys_GqlDataCaller/LocationFeedingCalls/GQLCalls/getLfStructureCall";

interface PenModalProps {
  pen?: LfLocation;
  sectionId: number;
  systemId: number;
  isModalOpen: boolean;
  onClose: () => void;
}

export default function PenModal({
  pen,
  sectionId,
  systemId,
  isModalOpen,
  onClose,
}: PenModalProps) {
  const { t } = useTranslation();
  const getUnit = useUnitsComponent();
  const [activePen, setActivePen] = useState(pen);
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  const { call: getLfSectionsInfo, data: lfSectionsState } =
    useSection(connection);
  const { call: getSystemCall, data: systemsState } = useSystem(connection);
  const animal =
    pen?.animals && pen.animals.length ? pen.animals[0] : undefined;
  const sections = useMemo(
    () => (lfSectionsState[domain] ? lfSectionsState[domain].data : undefined),
    [domain, lfSectionsState]
  );

  useEffect(() => {
    getLfSectionsInfo({ ids: [systemId] }, connection);
    getFeedCurves({ fetchAmount: 50, ifFeedCurveBaseInfoOnly: true });
    getSystemCall({ ids: [systemId] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setActivePen(pen);
  }, [pen]);

  const sectionName = sections?.find(
    (section) => section.section_id === sectionId
  )?.name;
  const systemName = systemsState[domain]?.data.find(
    (system) => system.systemId === systemId
  )?.name;

  // Pen Options
  const pensOptions = useMemo(() => {
    if (!sections || !sectionId) return [];

    return (
      sections.find((section) => section.section_id === sectionId)
        ?.section_locations ?? []
    );
  }, [sections, sectionId]);

  // Find currentIndex to toggle between pens
  const currentIndex = pensOptions.findIndex(
    (pen) => pen.location_id === activePen?.location_id
  );

  // #region | FeedCurve
  // FeedCurve Options
  const { call: getFeedCurves, data: feedCurvesState } =
    useFeedCurves(connection);
  const feedCurves = useMemo(() => {
    if (feedCurvesState && feedCurvesState[domain]) {
      return feedCurvesState[domain].data;
    }
    return [];
  }, [domain, feedCurvesState]);

  // FeedCurve Graph
  const feedCurveGraph = animal?.feedCurve;

  // FeedCurve Components
  const feedCurveComponents = animal?.feedCurve?.components?.map(
    (component) => {
      return (
        <div
          key={component.feedCurveComponentId}
          style={{
            ...css.colorTag,
            backgroundColor: getGraphColor(component.componentId),
            ...fonts.header,
            ...fonts.fontSize4,
            color: colors.whitePrimary,
          }}
        >
          {component.name}
        </div>
      );
    }
  );

  // FeedCurve Milestones
  const feedCurveMilestones = animal?.feedCurve?.milestones?.map(
    (milestone, index) => (
      <p
        key={index}
        style={{
          margin: "5px 0",
          ...fonts.paragraph,
          ...fonts.fontSize4,
          color: colors.greyDarkPrimary,
        }}
      >
        {milestone.name}
      </p>
    )
  );
  // #endregion

  // Progress Bar
  const animalFoodTotal = activePen
    ? activePen.animals.length
      ? activePen?.animals[0].consumptions?.[0].total
      : 0
    : 0;
  const animalFoodProgressed = activePen
    ? activePen.animals.length
      ? activePen?.animals[0].consumptions?.[0].progressed
      : 0
    : 0;

  // Seperator
  const seperator = (
    <div
      style={{
        width: "80%",
        minWidth: "280px",
        height: "2px",
        backgroundColor: colors.greyTertiary,
      }}
    />
  );

  return (
    <>
      <NewModal
        modalState={isModalOpen}
        header={`${t("operationsPages.pen")} ${activePen}`}
        subHeader={`${systemName} ${sectionName}`}
        overrides={{
          backgroundColor: colors.greyLightTertiary,
          mainContentScroll: true,
        }}
        predefinedButtons={{
          onClose: () => {
            onClose();
            setActivePen(pen);
          },
          onNext: () => {
            setActivePen(pensOptions[(currentIndex + 1) % pensOptions.length]);
          },
          onPrev: () => {
            setActivePen(
              pensOptions[
                (currentIndex - 1 + pensOptions.length) % pensOptions.length
              ]
            );
          },
        }}
        headerContent={
          <NewSelect
            key={activePen?.location_id}
            options={pensOptions.map((pen) => ({
              id: pen.location_id,
              label: pen.name,
            }))}
            onChange={(i) => {
              setActivePen(
                pensOptions.find((pen) => pen.location_id === i?.id)
              );
            }}
            fieldOverrides={{
              fieldWidth: "100px",
            }}
            valueOverrides={{
              defaultValue: pensOptions.length
                ? activePen
                  ? { id: activePen.location_id, label: activePen.name }
                  : {
                      id: pensOptions[0].location_id,
                      label: pensOptions[0].name,
                    }
                : undefined,
            }}
          />
        }
        mainContent={
          <div
            style={{
              width: "100%",
              padding: "20px",
              boxSizing: "border-box",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              gap: "20px",
            }}
          >
            {/* Animal from Location */}
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "center",
                gap: "5px",
              }}
            >
              <img
                src={icons.animalsColor}
                style={{ width: "40px" }}
                alt="icon"
              />
              <div>
                <p
                  style={{
                    ...fonts.header,
                    ...fonts.fontSize4,
                    color: colors.greyPrimary,
                  }}
                >
                  {t(`operationsPages.inThisPen`, {
                    defaultValue: "in this pen",
                  })}
                </p>
                <p
                  style={{
                    ...fonts.header,
                    ...fonts.fontSize3,
                    color: colors.greyDarkPrimary,
                  }}
                >{`${t("animalPages.animal.animal")} ${animal?.animal_id}`}</p>
              </div>
            </div>
            {seperator}
            {/* Activations and ProgressBar */}
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                gap: "20px",
                height: "100%",
              }}
            >
              {/* <Activations pen={activePen} showActivations /> */}
              <ProgressBar
                animation
                header={`${t("feed.general.remainingToday", {
                  defaultValue: "Remaining Today",
                })}`}
                total={animalFoodTotal ?? 0}
                progressed={animalFoodProgressed ?? 0}
                unit={getUnit(Units.kG_grey)}
              />
            </div>
            {seperator}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
              }}
            >
              {/* FeedCurve */}
              <NewSelect
                header={`${t("feed.feedCurve.feedCurve", {
                  defaultValue: "feed curve",
                })}`}
                options={feedCurves.map((feedCurve) => ({
                  label: feedCurve.name,
                  id: feedCurve.feedCurveId,
                }))}
                fieldOverrides={{
                  fieldStyle: NewFieldStyles.greyTertiary,
                }}
                valueOverrides={{
                  defaultValue: animal?.feedCurve?.name
                    ? {
                        label: animal.feedCurve.name,
                        id: animal.feedCurve.feedCurveId,
                      }
                    : undefined,
                }}
              />
              {/* FeedCorrection */}
              <FeedCorrectionPopover
                header={`${t("feed.feedCorrection.feedCorrection", {
                  defaultValue: "feed correction",
                })}`}
                fieldOverrides={{ fieldHeight: "30px" }}
                operationView
              />
              {/* FeedSkip */}
              <FeedSkipPopover
                pen={pen}
                header={`${t("feed.feedSkip.feedSkip", {
                  defaultValue: "feed skip",
                })}`}
                fieldOverrides={{ fieldHeight: "30px" }}
                operationView
              />
            </div>

            {/* FeedCurve Graph, Components, Milestone */}
            <div
              style={{
                backgroundColor: colors.greyLightTertiary,
                borderRadius: "0px 0px 5px 5px",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <p
                style={{
                  ...fonts.header,
                  ...fonts.fontSize3,
                  color: colors.greyDarkPrimary,
                }}
              >
                {t("operationsPages.today", { defaultValue: "today" })}
              </p>
              <p
                style={{
                  ...fonts.paragraph,
                  ...fonts.fontSize4,
                  color: colors.greyDarkPrimary,
                }}
              >{`${t("operationsPages.day", { defaultValue: "Day" })} ${
                animal?.milestone_day
              } ${t("operationsPages.since", { defaultValue: "since" })} ${
                animal?.feedcurve_milestone
              }`}</p>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(2, auto)",
                  gridTemplateRows: "repeat(2, auto)",
                  gap: "2px",
                  backgroundColor: colors.greyLightSecondary,
                }}
              >
                <div
                  style={{
                    gridArea: "1/1/2/3",
                    backgroundColor: colors.greyLightTertiary,
                  }}
                >
                  {feedCurveGraph ? (
                    <FeedCurveViewer data={feedCurveGraph} isMiniature />
                  ) : (
                    <></>
                  )}
                </div>
                <div
                  style={{
                    gridArea: "2/1/3/2",
                    backgroundColor: colors.greyLightTertiary,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: "5px",
                    paddingTop: "10px",
                  }}
                >
                  <p
                    style={{
                      ...fonts.header,
                      ...fonts.fontSize4,
                      color: colors.greyDarkPrimary,
                    }}
                  >
                    {t("feedCurvePages.components.components", {
                      defaultValue: "Components",
                    })}
                  </p>
                  {feedCurveComponents}
                </div>
                <div
                  style={{
                    gridArea: "2/2/3/3",
                    backgroundColor: colors.greyLightTertiary,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "start",
                    gap: "5px",
                    paddingTop: "10px",
                  }}
                >
                  <p
                    style={{
                      ...fonts.header,
                      ...fonts.fontSize4,
                      color: colors.greyDarkPrimary,
                    }}
                  >
                    {t("milestones.general.milestones", {
                      defaultValue: "Milestones",
                    })}
                  </p>
                  {feedCurveMilestones}
                </div>
              </div>
            </div>
          </div>
        }
      />
    </>
  );
}
